import React, { useEffect, useState } from "react";
import { LogOut, Zap, CheckCircle2, XCircle, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

export default function ArtistDashboard() {
  const { user, logout } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [requests, setRequests] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "",
    type: "merch",
    description: "",
    target_date: "",
    reference_image_url: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const [e, r, d] = await Promise.all([
        api.get("/portal/earnings").then((x) => x.data),
        api.get("/portal/requests").then((x) => x.data),
        api.get("/portal/deliveries").then((x) => x.data).catch(() => ({ deliveries: [] })),
      ]);
      setEarnings(e);
      setRequests(r);
      setDeliveries(d?.deliveries || []);
    } catch {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/portal/requests", form);
      setForm({
        title: "",
        type: "merch",
        description: "",
        target_date: "",
        reference_image_url: "",
        notes: "",
      });
      setShowForm(false);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const StatusIcon = ({ status }) => {
    if (status === "approved") return <CheckCircle2 size={16} className="text-kult-yellow" />;
    if (status === "rejected") return <XCircle size={16} className="text-red-400" />;
    return <Clock size={16} className="text-kult-muted" />;
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12" data-testid="page-artist-dashboard">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-10">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ ARTIST PORTAL</p>
          <h1 className="font-display text-5xl md:text-7xl mt-2 leading-none">
            HEY <span className="text-kult-pink">{user?.name}.</span>
          </h1>
          <p className="text-kult-muted mt-2 font-mono text-xs tracking-[0.25em]">
            {user?.email} · ROYALTY {user?.royalty_percent}%
          </p>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 border border-kult-border px-4 py-2 hover:border-kult-pink font-mono text-xs tracking-[0.25em]"
          data-testid="portal-logout"
        >
          <LogOut size={14} /> LOG OUT
        </button>
      </div>

      {loading ? (
        <p className="font-mono text-xs text-kult-muted">LOADING...</p>
      ) : (
        <>
          {/* EARNINGS */}
          <section className="mb-12" data-testid="artist-earnings">
            <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4">/ EARNINGS</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Stat label="GROSS" value={`€${earnings?.gross_revenue?.toFixed(2) || "0.00"}`} testId="stat-gross" />
              <Stat
                label="YOUR PAYOUT"
                value={`€${earnings?.payout?.toFixed(2) || "0.00"}`}
                accent
                testId="stat-payout"
              />
              <Stat label="UNITS SOLD" value={earnings?.units_sold || 0} testId="stat-units" />
            </div>
            <div className="mt-6 border border-kult-border">
              <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-kult-border bg-kult-black2 font-mono text-[10px] tracking-[0.3em] text-kult-muted">
                <span className="col-span-7">PRODUCT</span>
                <span className="col-span-2 text-right">UNITS</span>
                <span className="col-span-3 text-right">GROSS</span>
              </div>
              {(earnings?.by_product || []).length === 0 && (
                <p className="px-5 py-6 font-mono text-sm text-kult-muted">
                  No sales yet. When orders come in, they'll show up here.
                </p>
              )}
              {(earnings?.by_product || []).map((p, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-kult-border last:border-b-0"
                  data-testid={`earnings-row-${i}`}
                >
                  <span className="col-span-7 font-display text-lg">{p.name}</span>
                  <span className="col-span-2 text-right font-mono text-sm">×{p.units}</span>
                  <span className="col-span-3 text-right font-mono text-sm">
                    €{p.gross.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* DELIVERIES — hand-delivery orders assigned to this roster member */}
          <section className="mb-12" data-testid="artist-deliveries">
            <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-4">
              / DELIVERIES TO HAND OVER
              {deliveries.length > 0 && (
                <span className="ml-2 bg-kult-pink text-black px-2 py-0.5">{deliveries.length}</span>
              )}
            </p>
            <p className="text-kult-muted text-sm mb-4">
              These customers chose to pick up their order from <strong>you</strong>. Coordinate with them directly using the contact info below.
            </p>
            <div className="border border-kult-border">
              <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-kult-border bg-kult-black2 font-mono text-[10px] tracking-[0.3em] text-kult-muted">
                <span className="col-span-3">ORDER</span>
                <span className="col-span-3">CUSTOMER</span>
                <span className="col-span-3">CONTACT</span>
                <span className="col-span-3">ITEMS</span>
              </div>
              {deliveries.length === 0 ? (
                <p className="px-5 py-6 font-mono text-sm text-kult-muted">
                  No pending hand-deliveries. When a customer picks "Pick-up with you" at checkout, the order shows up here once paid.
                </p>
              ) : (
                deliveries.map((d) => (
                  <div
                    key={d.order_number}
                    className="grid grid-cols-12 gap-3 px-5 py-4 border-b border-kult-border last:border-b-0 items-start"
                    data-testid={`delivery-${d.order_number}`}
                  >
                    <div className="col-span-3 min-w-0">
                      <p className="font-mono text-[11px] text-white truncate">{d.order_number}</p>
                      <p className="font-mono text-[10px] text-kult-yellow mt-1 uppercase">{d.status}</p>
                    </div>
                    <div className="col-span-3 min-w-0">
                      <p className="font-display text-lg leading-tight truncate">{d.customer_name || "—"}</p>
                      {d.notes && <p className="font-mono text-[10px] text-kult-muted truncate mt-1" title={d.notes}>"{d.notes}"</p>}
                    </div>
                    <div className="col-span-3 min-w-0 font-mono text-[11px]">
                      <a href={`mailto:${d.customer_email}`} className="block truncate text-white hover:text-kult-pink" title={d.customer_email}>
                        {d.customer_email}
                      </a>
                      {d.customer_phone ? (
                        <a href={`tel:${d.customer_phone}`} className="block truncate text-kult-yellow hover:text-kult-pink mt-1">
                          ☎ {d.customer_phone}
                        </a>
                      ) : (
                        <span className="text-kult-muted">No phone</span>
                      )}
                    </div>
                    <div className="col-span-3 min-w-0">
                      {(d.items || []).map((it, i) => (
                        <p key={i} className="font-mono text-[11px] text-kult-muted truncate">
                          ×{it.quantity} {it.name}{it.size ? ` / ${it.size}` : ""}
                        </p>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          {/* REQUESTS */}
          <section data-testid="artist-requests">
            <div className="flex items-center justify-between mb-4">
              <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ MY REQUESTS</p>
              <button
                onClick={() => setShowForm((s) => !s)}
                className="bg-kult-pink text-black px-5 py-2 font-display text-lg hover:bg-kult-yellow transition-colors"
                data-testid="portal-new-request-toggle"
              >
                + NEW REQUEST
              </button>
            </div>

            {showForm && (
              <form
                onSubmit={submit}
                className="border-2 border-kult-pink p-6 bg-kult-black2 mb-6 space-y-4"
                data-testid="portal-request-form"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="TITLE" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} testId="req-title" />
                  <SelectField
                    label="TYPE"
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    options={[
                      { v: "merch", l: "MERCH IDEA" },
                      { v: "event", l: "EVENT IDEA" },
                    ]}
                    testId="req-type"
                  />
                  <Field
                    label="TARGET DATE"
                    type="date"
                    value={form.target_date}
                    onChange={(e) => setForm({ ...form, target_date: e.target.value })}
                    testId="req-target-date"
                  />
                  <Field
                    label="REFERENCE IMAGE URL"
                    type="url"
                    value={form.reference_image_url}
                    onChange={(e) => setForm({ ...form, reference_image_url: e.target.value })}
                    placeholder="https://..."
                    testId="req-image"
                  />
                  <TextareaField
                    label="DESCRIPTION"
                    required
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="md:col-span-2"
                    testId="req-description"
                  />
                  <TextareaField
                    label="NOTES"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="md:col-span-2"
                    testId="req-notes"
                  />
                </div>
                {error && <p className="font-mono text-xs text-kult-pink">{error}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  className="bg-kult-pink text-black font-display text-xl py-3 px-6 hover:bg-kult-yellow disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="req-submit"
                >
                  <Zap size={18} /> {submitting ? "..." : "SUBMIT REQUEST"}
                </button>
              </form>
            )}

            {requests.length === 0 ? (
              <p className="font-mono text-sm text-kult-muted py-6">
                No requests yet. Hit "NEW REQUEST" above.
              </p>
            ) : (
              <div className="space-y-3">
                {requests.map((r) => (
                  <article
                    key={r.id}
                    className="border border-kult-border p-5 hover:border-kult-pink"
                    data-testid={`my-request-${r.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-[10px] tracking-[0.3em] bg-kult-yellow text-black px-2 py-0.5">
                            {r.type === "merch" ? "MERCH" : "EVENT"}
                          </span>
                          <span className="font-mono text-[10px] tracking-[0.25em] text-kult-muted">
                            {new Date(r.created_at).toLocaleDateString()}
                          </span>
                          <span className="inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.25em] uppercase">
                            <StatusIcon status={r.status} /> {r.status}
                          </span>
                        </div>
                        <h3 className="font-display text-2xl">{r.title}</h3>
                        <p className="text-kult-muted text-sm mt-1">{r.description}</p>
                        {r.admin_response && (
                          <p className="mt-3 border-l-2 border-kult-pink pl-3 text-sm">
                            <span className="font-mono text-[10px] tracking-[0.3em] text-kult-pink">
                              ADMIN ·
                            </span>{" "}
                            {r.admin_response}
                          </p>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div
      className={`border-2 ${accent ? "border-kult-pink" : "border-kult-border"} p-6 ${
        accent ? "bg-kult-black2" : ""
      }`}
      data-testid={testId}
    >
      <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted">{label}</p>
      <p
        className={`font-display text-5xl mt-2 ${accent ? "text-kult-pink" : "text-white"}`}
      >
        {value}
      </p>
    </div>
  );
}

function Field({ label, className = "", testId, ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">
        {label}
      </span>
      <input
        {...props}
        data-testid={testId}
        className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
      />
    </label>
  );
}

function TextareaField({ label, className = "", testId, ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">
        {label}
      </span>
      <textarea
        rows={3}
        {...props}
        data-testid={testId}
        className="w-full bg-transparent border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
      />
    </label>
  );
}

function SelectField({ label, options, testId, ...props }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">
        {label}
      </span>
      <select
        {...props}
        data-testid={testId}
        className="w-full bg-kult-black border border-kult-border px-3 py-3 font-mono text-sm focus:border-kult-pink outline-none"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
    </label>
  );
}
